import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewState, User, UserRole } from '../types';
import { Brain, LayoutDashboard, ClipboardList, History, FileText, Users as UsersIcon, CreditCard, User as UserIcon, LogOut, Shield, ShieldCheck, Home } from 'lucide-react';
import { LanguageSwitcher } from './i18n/LanguageSwitcher';

interface SidebarProps {
  user: User;
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  onLogout: () => void;
  evolutivosEnabled: boolean;
  alertCount?: number;
  criticalCount?: number;
  onOpenAlerts?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ user, currentView, onNavigate, onLogout, evolutivosEnabled, alertCount = 0, criticalCount = 0, onOpenAlerts }) => {
  const { t } = useTranslation('common');
  const [showProfileTray, setShowProfileTray] = useState(false);

  const isDashboard = currentView === 'DASHBOARD';
  const isTests = currentView === 'TEST_LIST' || (typeof currentView === 'object' && currentView.type === 'TAKE_TEST');
  const isHistory = currentView === 'HISTORY' || (typeof currentView === 'object' && currentView.type === 'VIEW_REPORT');
  const isEvolutivos = currentView === 'EVOLUTIVOS' || (typeof currentView === 'object' && (currentView.type === 'EVOLUTIVO_DETAIL' || currentView.type === 'CREATE_EVOLUTIVO'));
  const isPatients = currentView === 'PATIENTS_LIST' || (typeof currentView === 'object' && currentView.type === 'PATIENT_PROFILE');
  const isBatteries = currentView === 'BATTERIES' || (typeof currentView === 'object' && (currentView.type === 'BATTERY_RUNNER' || currentView.type === 'BATTERY_REPORT'));
  const isUsers = currentView === 'USERS_MANAGEMENT';
  const isSettings = currentView === 'SETTINGS';
  const isProfile = currentView === 'PROFILE';
  const isAdminPanel = currentView === 'ADMIN_DASHBOARD';
  const isSuperAdminPanel = currentView === 'SUPERADMIN_DASHBOARD';
  const showManagementNav = user.role === UserRole.ADMIN || user.role === UserRole.SUPERADMIN;

  const showEvolutivos = evolutivosEnabled || user.role === UserRole.SUPERADMIN;

  const mainNavItems = [
    { id: 'DASHBOARD', icon: LayoutDashboard, label: t('navigation.dashboard'), isActive: isDashboard, dataTour: 'dashboard' },
    { id: 'TEST_LIST', icon: ClipboardList, label: t('navigation.instruments'), isActive: isTests, dataTour: 'tests' },
    { id: 'HISTORY', icon: History, label: t('navigation.history'), isActive: isHistory, dataTour: 'history' },
    ...(showEvolutivos ? [{ id: 'EVOLUTIVOS', icon: FileText, label: t('navigation.evolutivos'), isActive: isEvolutivos, dataTour: 'evolutivos' }] : []),
    { id: 'PATIENTS_LIST', icon: UsersIcon, label: t('navigation.patients'), isActive: isPatients, dataTour: 'patients' },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-64 bg-slate-900 text-white flex-col h-screen fixed left-0 top-0 no-print">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-blue-400">Psymetrika</h1>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {user.role === UserRole.ADMIN && (
            <button
              onClick={() => onNavigate('ADMIN_DASHBOARD')}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                isAdminPanel ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <Shield className="w-5 h-5 mr-3" />
              {t('navigation.adminPanel')}
            </button>
          )}

          {user.role === UserRole.SUPERADMIN && (
            <button
              onClick={() => onNavigate('SUPERADMIN_DASHBOARD')}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                isSuperAdminPanel ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <ShieldCheck className="w-5 h-5 mr-3" />
              {t('navigation.superadminPanel')}
            </button>
          )}

          {showManagementNav && (
            <button
              onClick={() => onNavigate('USERS_MANAGEMENT')}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                isUsers ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
              {t('navigation.users')}
            </button>
          )}

          {showManagementNav && <div className="my-3 h-px bg-slate-700/70" />}

          <button
            data-tour="dashboard"
            onClick={() => onNavigate('DASHBOARD')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              isDashboard ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path></svg>
            {t('navigation.dashboard')}
          </button>


          <button
            data-tour="patients"
            onClick={() => onNavigate('PATIENTS_LIST')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              isPatients ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
            {t('navigation.patients')}
          </button>

          <button
            data-tour="tests"
            onClick={() => onNavigate('TEST_LIST')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              isTests ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
            {t('navigation.instruments')}
          </button>

          <button
            data-tour="history"
            onClick={() => onNavigate('HISTORY')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              isHistory ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            {t('navigation.history')}
          </button>

          {showEvolutivos && (
            <button
              data-tour="evolutivos"
              onClick={() => onNavigate('EVOLUTIVOS')}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                isEvolutivos ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
              {t('navigation.evolutivos')}
            </button>
          )}

          <button
            data-tour="batteries"
            onClick={() => onNavigate('BATTERIES')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              isBatteries ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {t('navigation.batteries')}
          </button>

          <button
            onClick={() => onNavigate('SUBSCRIPTION')}
            className={`w-full flex items-center p-3 rounded-lg transition-colors ${
              currentView === 'SUBSCRIPTION' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
            </svg>
            {t('navigation.subscription')}
          </button>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <button
            data-tour="profile"
            onClick={() => onNavigate('PROFILE')}
            className={`w-full flex items-center mb-3 p-2 rounded-lg transition-colors ${
              isProfile ? 'bg-slate-800 text-white' : 'hover:bg-slate-800'
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold">
              {user.name.charAt(0)}
            </div>
            <div className="ml-3 flex-1 text-left">
              <p className="text-sm font-medium">{user.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user.role.toLowerCase()}</p>
            </div>
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="flex items-center justify-between">
            <button
              onClick={onLogout}
              className="text-xs text-red-300 hover:text-red-100 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
              {t('navigation.logout')}
            </button>
            <div className="flex items-center gap-2">
              {onOpenAlerts && (
                <button
                  onClick={onOpenAlerts}
                  className="relative text-slate-400 hover:text-white transition-colors"
                  title="Alertas clínicas"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {alertCount > 0 && (
                    <span className={`absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full text-[8px] font-bold flex items-center justify-center px-0.5 ${
                      criticalCount > 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
                    }`}>
                      {alertCount > 9 ? '9+' : alertCount}
                    </span>
                  )}
                </button>
              )}
              <a
                href="/"
                className="text-slate-400 hover:text-white transition-colors"
                title={t('navigation.home')}
              >
                <Home className="w-4 h-4" />
              </a>
              <LanguageSwitcher variant="minimal" darkMode />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 no-print shadow-lg">
        <div className={`grid ${showEvolutivos ? 'grid-cols-6' : 'grid-cols-5'} h-14`}>
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                data-tour={item.dataTour}
                onClick={() => {
                  onNavigate(item.id as ViewState);
                  setShowProfileTray(false);
                }}
                className={`flex flex-col items-center justify-center gap-0.5 transition-colors px-1 ${
                  item.isActive
                    ? 'text-blue-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[9px] font-medium truncate max-w-full">{item.label}</span>
              </button>
            );
          })}
          {/* Profile button */}
          <button
            onClick={() => setShowProfileTray((prev) => !prev)}
            className={`flex flex-col items-center justify-center gap-0.5 transition-colors px-1 ${
              isProfile || showProfileTray ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            <span className="text-[9px] font-medium">{t('navigation.profile')}</span>
          </button>
        </div>
      </nav>

      {/* Mobile Profile Tray */}
      {showProfileTray && (
        <div className="lg:hidden fixed bottom-16 right-3 z-50">
          <div className="bg-white border border-slate-200 shadow-lg rounded-full px-2 py-1 flex items-center gap-1">
            <button
              data-tour="profile"
              onClick={() => {
                onNavigate('PROFILE');
                setShowProfileTray(false);
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-600 hover:bg-slate-100 text-xs font-medium"
            >
              <UserIcon className="w-4 h-4" />
              {t('navigation.profile')}
            </button>
            <button
              onClick={() => {
                onNavigate('SUBSCRIPTION');
                setShowProfileTray(false);
              }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium ${
                currentView === 'SUBSCRIPTION' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              {t('navigation.subscription')}
            </button>
            <button
              onClick={() => {
                onNavigate('BATTERIES');
                setShowProfileTray(false);
              }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium ${
                isBatteries ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {t('navigation.batteries')}
            </button>
            {(user.role === UserRole.ADMIN || user.role === UserRole.SUPERADMIN) && (
              <button
                onClick={() => {
                  onNavigate('USERS_MANAGEMENT');
                  setShowProfileTray(false);
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium ${
                  isUsers ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <UsersIcon className="w-4 h-4" />
                {t('navigation.users')}
              </button>
            )}
            {user.role === UserRole.ADMIN && (
              <button
                onClick={() => {
                  onNavigate('ADMIN_DASHBOARD');
                  setShowProfileTray(false);
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium ${
                  isAdminPanel ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Shield className="w-4 h-4" />
                {t('navigation.adminPanel')}
              </button>
            )}
            {user.role === UserRole.SUPERADMIN && (
              <button
                onClick={() => {
                  onNavigate('SUPERADMIN_DASHBOARD');
                  setShowProfileTray(false);
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium ${
                  isSuperAdminPanel ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                {t('navigation.superadminPanel')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
